// Careers data prefetch. App.tsx calls prefetchCareers(location.pathname) at
// module scope, so on a hard load of /careers or /careers/:id the Firestore
// chunk downloads alongside the lazy route chunk and the read starts as soon as
// Firestore is ready, not after React mounts the page. Firestore is only ever
// imported dynamically here (type imports are erased), so `/` stays Firebase-free.
import type { DocumentSnapshot, QuerySnapshot } from "firebase/firestore/lite";

/** Same pair Contact.tsx loads, so webpack shares the chunks. */
export const loadFirestore = () => Promise.all([import("firebase/firestore/lite"), import("./firebaseDb")]);

const noop = () => {};
const jobDocs = new Map<string, Promise<DocumentSnapshot>>();
let openJobs: Promise<QuerySnapshot> | null = null;

/** Starts (or reuses) the read of one posting. Callers consume it once via takeJob. */
export function prefetchJob(id: string): Promise<DocumentSnapshot> {
  let p = jobDocs.get(id);
  if (!p) {
    p = loadFirestore().then(([{ doc, getDoc }, { db }]) => getDoc(doc(db, "jobPostings", id)));
    p.catch(noop); // the consumer handles errors; avoid an unhandled rejection meanwhile
    jobDocs.set(id, p);
  }
  return p;
}

/** Returns the prefetched read (or starts one) and drops it from the cache so a revisit refetches. */
export function takeJob(id: string): Promise<DocumentSnapshot> {
  const p = prefetchJob(id);
  jobDocs.delete(id);
  return p;
}

export function prefetchOpenJobs(): Promise<QuerySnapshot> {
  if (!openJobs) {
    openJobs = loadFirestore().then(([{ collection, getDocs, query, where }, { db }]) =>
      getDocs(
        query(collection(db, "jobPostings"), where("status", "==", "open"), where("visibility", "==", "public"))
      )
    );
    openJobs.catch(noop);
  }
  return openJobs;
}

export function takeOpenJobs(): Promise<QuerySnapshot> {
  const p = prefetchOpenJobs();
  openJobs = null;
  return p;
}

/** Hard-load entry point: start the careers data for /careers and /careers/:id. */
export function prefetchCareers(pathname: string): void {
  const m = /^\/careers(?:\/([^/]+))?\/?$/.exec(pathname);
  if (!m) return;
  if (!m[1]) {
    prefetchOpenJobs();
    return;
  }
  let id: string;
  try {
    id = decodeURIComponent(m[1]);
  } catch {
    return;
  }
  prefetchJob(id);
}
